/* eslint-disable react/display-name */
import React from 'react';
import ReactSelect, { components } from 'react-select';
import styled, { css, useTheme } from 'styled-components';
import { spacing } from 'ui/theme/utils';
import _ from 'lodash';

export const Select = ({
  required,
  disabled,
  value,
  useRawValue,
  options: initialOptions,
  onChange,
  minWidth,
  minHeight,
  color,
  bold,
  placeholder = '',
  placeholderOpacity,
  descriptive,
  grouped,
  valueRenderer = (option) => option.label,
  optionColor,
  caretColor,
  zIndex = 2,
  styles: customStyles,
  components: customComponents,
  isSearchable = false,
  isClearable,
  isMulti,
  fitSize,
  icon,
  ...rest
}) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = React.useState();
  const theme = useTheme();
  const options = React.useMemo(
    () =>
      grouped && initialOptions
        ? _.flatten(initialOptions.map((option) => option.options))
        : initialOptions,
    [grouped, initialOptions]
  );
  const groupedOptions = React.useMemo(
    () =>
      !grouped && descriptive ? [{ label: placeholder, options: initialOptions }] : initialOptions,
    [grouped, descriptive, placeholder, initialOptions]
  );

  React.useLayoutEffect(() => {
    if (!options || useRawValue) return;
    const option = value?.value ? value : options.find((opt) => opt.value === value);
    if (option?.value !== options[selectedOptionIndex]?.value) {
      selectOption(option);
    }
  }, [value, options]);

  const selectOption = (option) => {
    if (!options || useRawValue) return;
    const index = options.findIndex((opt) => opt.value === option?.value);
    setSelectedOptionIndex(index);
  };

  const handleChange = (option) => {
    if (onChange) {
      onChange(option);
    } else {
      selectOption(option);
    }
  };

  return (
    <>
      <StyledSelect
        {...rest}
        styles={getCustomStyles({
          theme,
          minWidth,
          minHeight,
          color,
          bold,
          placeholderOpacity,
          optionColor,
          zIndex,
          grouped,
          fitSize,
          customStyles: {
            ...customStyles,
            menuList: customComponents?.MenuFooter ? { paddingBottom: 0 } : {},
          },
        })}
        placeholder={placeholder}
        isSearchable={isSearchable}
        isClearable={isClearable}
        disabled={disabled}
        isMulti={isMulti}
        //menuIsOpen={true} // uncomment to freely style the menu on dev env
        components={{
          Menu,
          ...customComponents,
          Control:
            !icon || isSearchable
              ? components.Control
              : (props) => <Control icon={icon} {...props} />,
          IndicatorSeparator: isClearable && value ? components.IndicatorSeparator : () => null,
          DropdownIndicator: (props) => <DropdownIndicator caretColor={caretColor} {...props} />,
          SingleValue: (props) => <SingleValue renderer={valueRenderer} {...props} />,
        }}
        options={groupedOptions || []}
        //value={value} // keeps focusing the first option @see https://github.com/JedWatson/react-select/pull/4080
        value={useRawValue || isMulti ? value : options[selectedOptionIndex] || ''}
        onChange={handleChange}
      />
      {required && !disabled && <HiddenInput value={value} required />}
    </>
  );
};

const Menu = (props) => {
  const { MenuFooter = null } = props.selectProps.components;

  return (
    <components.Menu {...props}>
      {props.children}
      {MenuFooter}
    </components.Menu>
  );
};

const Control = ({ icon: Icon, children, ...rest }) => (
  <components.Control {...rest}>
    {Icon && <Icon />}
    {children}
  </components.Control>
);

const SingleValue = ({ renderer, ...props }) => (
  <components.SingleValue {...props}>{renderer(props.data)}</components.SingleValue>
);

const getCustomStyles = ({
  theme,
  minWidth,
  minHeight,
  color,
  bold,
  placeholderOpacity,
  optionColor,
  zIndex,
  grouped,
  fitSize,
  customStyles = {},
}) => {
  const styles = {
    value: bold
      ? {
          fontWeight: 500,
          fontSize: '14px',
          letterSpacing: '0.04em',
        }
      : {},
    option: bold
      ? {
          fontWeight: 400,
          fontSize: '14px',
          letterSpacing: '0.04em',
        }
      : {},
    group: {
      padding: 0,
    },
    groupHeading: {
      padding: '8px 12px',
      marginBottom: 0,
    },
  };

  if (fitSize) {
    styles.value.position = 'relative';
    styles.value.top = 0;
    styles.value.transform = 'none';
    styles.value.maxWidth = 'initial';
    //if (!minHeight) minHeight = 'auto';
  }

  if (grouped) {
    styles.group.borderBottom = '1px solid #eee';
    styles.group[':last-child'] = {
      borderBottom: 0,
    };

    styles.groupHeading.color = '#333';
    styles.groupHeading.fontWeight = 'bold';
    styles.groupHeading.fontSize = '11px';

    styles.option.paddingLeft = '24px';
  } else {
    styles.groupHeading.borderBottom = '1px solid #eee';
  }

  return {
    control: (provided) => ({
      ...provided,
      border: 0,
      boxShadow: 'none',
      borderRadius: 'inherit',
      backgroundColor: 'transparent',
      minWidth: minWidth || 'inherit',
      minHeight: minHeight || provided.minHeight,
    }),
    menu: (provided) => ({
      ...provided,
      minWidth: '100%',
      width: 'auto',
      zIndex,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: color || provided.color,
      opacity: placeholderOpacity || 1,
      ...styles.value,
    }),
    group: (provided) => ({
      ...provided,
      ...styles.group,
    }),
    groupHeading: (provided) => ({
      ...provided,
      ...styles.groupHeading,
      ...customStyles.groupHeading,
    }),
    menuList: (provided) => ({
      ...provided,
      ...customStyles.menuList,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: color || theme.colors.grey,
      ...styles.value,
    }),
    valueContainer: (provided) => ({
      ...provided,
      paddingRight: 0,
      flexWrap: 'initial',
    }),
    option: (provided, { isSelected, isFocused }) => ({
      ...provided,
      backgroundColor: isSelected ? theme.colors.quaternary : isFocused ? '#f7f7f7' : null,
      color: isSelected ? theme.colors.primary : optionColor || null,
      ...styles.option,
      ':active': {
        backgroundColor: null,
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#ccc',
      ':hover': {
        color: '#f74b4b',
      },
      '> svg': {
        width: '18px',
        height: '18px',
      },
    }),
  };
};

const StyledSelect = styled(ReactSelect)`
  background-color: ${({ bg = 'white', theme }) => theme.colors[bg] || bg};
  color: ${({ theme }) => theme.colors.grey};
  ${({ variant }) => {
    switch (variant) {
      case 'minimal':
        return css`
          box-shadow: ${({ theme }) => theme.shadows.round};
          border: none;
        `;
      case 'outline':
        return css`
          border: 1px solid #ddd;
        `;
      default:
        return css`
          box-shadow: ${({ boxShadow = 'bottom', theme }) => theme.shadows[boxShadow] || boxShadow};
          border: none;
        `;
    }
  }}
  padding: 0;
  border-radius: ${({ borderRadius = '8px' }) => borderRadius};
  width: ${({ width = '100%' }) => width};
  text-align: left;
  letter-spacing: 0.62px;
  font-weight: 400;
  font-size: 13px;
  ${(props) => spacing(props)}
  ${({ disabled }) =>
    disabled &&
    `
    pointer-events: none;
    opacity: 0.5;
  `}
`;

const HiddenInput = styled.input.attrs(() => ({
  tabIndex: -1,
  autoComplete: 'off',
  onChange: () => {},
}))`
  opacity: 0;
  width: 100%;
  height: 0;
  position: absolute;
  bottom: 0;
  z-index: -1;
`;

const CaretDownIcon = styled.span.attrs(() => ({
  children: (
    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5">
      <path
        id="Polygone_6"
        data-name="Polygone 6"
        d="M4,0,8,5H0Z"
        transform="translate(8 5) rotate(180)"
      />
    </svg>
  ),
}))`
  display: flex;
  align-items: center;
  padding: 4px;
  > svg {
    fill: ${({ theme, caretColor = '#888' }) => theme.colors[caretColor] || caretColor};
  }
`;

const DropdownIndicator = ({ caretColor, ...props }) => {
  return (
    <components.DropdownIndicator {...props}>
      <CaretDownIcon caretColor={caretColor} />
    </components.DropdownIndicator>
  );
};
